// Client Internal Audit — the audit plan exactly as the client sees it. Used by the office job page (live preview)
// and the public view-only plan page (/ia-plan/:token). The CCIS standard plan (lib/iaPlan.js), day by day for a
// multi-day audit; light rules and a CCIS teal header row.
import React from 'react'
import { oneLine } from '../lib/oneLine'
import { planSchedule, planTitle } from '../lib/iaPlan'
import { longDate } from '../lib/iaReport'

const SHORT = { 'ISO 9001:2015': 'ISO 9001', 'ISO 14001:2015': 'ISO 14001', 'ISO 45001:2018': 'ISO 45001', 'ISO/IEC 27001:2022': 'ISO/IEC 27001', 'ISO/IEC 42001:2023': 'ISO/IEC 42001',
  'ISO 22301:2019': 'ISO 22301', 'SS 651:2019': 'SS 651', 'ISO/IEC 17025:2017': 'ISO/IEC 17025', 'ISO 37001:2025': 'ISO 37001', 'ISO 50001:2018': 'ISO 50001', 'ISO 46001:2019': 'ISO 46001' }
const TEAL = '#0e5c5c', INK = '#1d2b2b', MUTED = '#5c6b6a', LINE = '#d6e0df', SOFT = '#f2f7f7', BREAK = '#fbf6ec'
const FONT = '"Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif'
// whiteSpace 'normal': App.css makes header cells nowrap.
const cell = { border: `1px solid ${LINE}`, padding: '7px 10px', verticalAlign: 'top', fontSize: 12.5, lineHeight: 1.45, color: INK, whiteSpace: 'normal' }
// 1px inside the page edge: with collapsed borders half the outer line falls outside the table box and the right-hand
// border is clipped in print.
const table = { width: 'calc(100% - 2px)', margin: '0 1px', borderCollapse: 'collapse' }
const th = { ...cell, background: TEAL, color: '#fff', fontWeight: 600, fontSize: 12, borderColor: TEAL, verticalAlign: 'bottom' }
const label = { ...cell, width: '24%', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: MUTED, background: SOFT, verticalAlign: 'middle' }

// contact: the client's contact person { name, phone } (Clients page). A sent plan carries its own copy
// (plan.contact), taken when the plan was sent.
export default function IAPlanDocument({ company, auditDate, auditors, standards, plan, contact }) {
  const std = (plan?.standards && plan.standards.length ? plan.standards : standards) || []
  const pic = plan?.contact || contact || {}
  const days = planSchedule(plan, auditDate)
  const multi = days.length > 1
  const names = { a1: (auditors || [])[0], a2: (auditors || [])[1] }
  const dateText = d => d.date ? longDate(d.date) : 'Date to be confirmed'
  // Plans with no clause numbers (only rows added by hand) — no clause columns then.
  const anyClause = (plan?.items || []).some(it => (it.clauses || []).some(Boolean))
  const nStd = anyClause ? Math.max(std.length, 1) : 0
  const colSpanAll = 3 + nStd
  return (
    <div style={{ fontFamily: FONT, color: INK, background: '#fff' }}>
      <div style={{ textAlign: 'center', fontWeight: 700, fontSize: 16, letterSpacing: '0.03em', color: TEAL, marginBottom: 14 }}>{planTitle(std)}</div>
      <table style={{ ...table, marginBottom: 16 }}>
        <tbody>
          <tr><td style={label}>Company</td><td style={{ ...cell, fontWeight: 600 }}>{company || '—'}</td></tr>
          <tr><td style={label}>Audit date</td><td style={cell}>
            {days.map(d => <div key={d.n}>{multi ? `Day ${d.n}: ` : ''}{dateText(d)} ({d.from} – {d.to})</div>)}</td></tr>
          {/* The audit site only — never the company address (as bizSAFE). */}
          <tr><td style={label}>Audit location</td><td style={cell}>{oneLine(plan?.location) || 'To be confirmed'}</td></tr>
          <tr><td style={label}>Contact person</td><td style={cell}>{[pic.name, pic.phone].filter(Boolean).join(' · ') || '—'}</td></tr>
          <tr><td style={label}>Audit team</td><td style={cell}>{(auditors || []).filter(Boolean).join(', ') || 'To be confirmed'}</td></tr>
          <tr><td style={label}>Audit criteria</td><td style={cell}>{std.join(', ') || '—'}</td></tr>
        </tbody>
      </table>
      <table style={table}>
        <thead>
          <tr>
            <th style={{ ...th, width: 96 }}>Time</th>
            <th style={th}>Element</th>
            {anyClause && (std.length ? std.map(s => <th key={s} style={{ ...th, width: 70, textAlign: 'center' }}>{SHORT[s] || s}<div style={{ fontWeight: 400, fontSize: 10, opacity: 0.85 }}>Clause</div></th>)
              : <th style={{ ...th, width: 70, textAlign: 'center' }}>Clause</th>)}
            <th style={{ ...th, width: 128, textAlign: 'center' }}>Auditee</th>
          </tr>
        </thead>
        <tbody>
          {days.map(day => (
            <React.Fragment key={day.n}>
              {multi && <tr><td colSpan={colSpanAll} style={{ ...cell, background: TEAL, borderColor: TEAL, color: '#fff', fontWeight: 600, fontSize: 12.5 }}>
                Day {day.n} – {dateText(day)}{day.who !== 'both' && names[day.who] ? ` · Auditor: ${names[day.who]}`
                  : names.a1 && names.a2 ? ` · Auditors: ${names.a1}, ${names.a2}` : ''}</td></tr>}
              <DayRows rows={day.rows} anyClause={anyClause} std={std} colSpanAll={colSpanAll} k={day.n} />
            </React.Fragment>))}
        </tbody>
      </table>
    </div>
  )
}

function DayRows({ rows, anyClause, std, colSpanAll, k }) {
  return rows.map((r, ri0) => {
    const ri = `${k}-${ri0}`
    if (r.kind !== 'items') {
      const brk = r.kind === 'break'
      return (
        <tr key={ri}>
          <td style={{ ...cell, fontWeight: 600, whiteSpace: 'nowrap', color: brk ? MUTED : TEAL, background: brk ? BREAK : SOFT }}>{r.time}</td>
          <td style={{ ...cell, fontWeight: 600, color: brk ? MUTED : INK, background: brk ? BREAK : SOFT }} colSpan={colSpanAll - 1}>{r.text}</td>
        </tr>
      )
    }
    // As the CCIS Word plans: the block's time is written once down the Time column, and the auditee once per
    // run of rows with the same auditee (instead of repeating "TOP MGT / MGT REPS / FUNCTIONAL HEADS" on every row).
    const span = r.items.map((it, ii) => {
      if (it.heading || (ii > 0 && !r.items[ii - 1].heading && r.items[ii - 1].who === it.who)) return 0
      let n = 1
      while (r.items[ii + n] && !r.items[ii + n].heading && r.items[ii + n].who === it.who) n++
      return n
    })
    const timeCell = <td rowSpan={r.items.length} style={{ ...cell, fontWeight: 600, color: TEAL, whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{r.time}</td>
    return r.items.map((it, ii) => it.heading
      ? <tr key={`${ri}-${ii}`}>{ii === 0 && timeCell}
          <td style={{ ...cell, fontWeight: 700, fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: TEAL, paddingTop: 10 }} colSpan={colSpanAll - 1}>{it.element}</td></tr>
      : (
        <tr key={`${ri}-${ii}`}>
          {ii === 0 && timeCell}
          <td style={{ ...cell, whiteSpace: 'pre-line', fontWeight: it.bold ? 600 : 400, ...(it.closeout ? { verticalAlign: 'middle' } : {}) }}>{it.element}</td>
          {anyClause && (std.length ? std : ['']).map((s, si) => <td key={si} style={{ ...cell, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>{(it.clauses || [])[si] || ''}</td>)}
          {span[ii] > 0 && <td rowSpan={span[ii]} style={{ ...cell, fontSize: 11, fontWeight: 600, letterSpacing: '0.03em', color: MUTED, verticalAlign: 'middle', textAlign: 'center', whiteSpace: 'pre-line' }}>{it.who}</td>}
        </tr>
      ))
  })
}
